import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, UsageResponse, Transcript, Summary } from '../api/client';

/**
 * Dashboard View
 * 
 * Displays recent calls with summaries and quick stats.
 * Requirements: 8.3
 */

interface CallWithSummary {
  transcript: Transcript;
  summary?: Summary;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [recentCalls, setRecentCalls] = useState<CallWithSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    try {
      setLoading(true);
      setError(null);
      
      const [usageData, callsData] = await Promise.all([
        api.getUsage().catch(() => null),
        api.getRecentCalls(10).catch(() => []),
      ]);
      
      setUsage(usageData);
      setRecentCalls(callsData);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function getSentimentClass(sentiment?: string): string {
    switch (sentiment) {
      case 'positive': return 'sentiment-positive';
      case 'negative': return 'sentiment-negative';
      case 'mixed': return 'sentiment-mixed';
      default: return 'sentiment-neutral';
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        Loading dashboard...
      </div>
    );
  }

  return (
    <div>
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">
            {usage?.callsProcessed ?? 0}
          </div>
          <div className="stat-label">
            Calls This Month
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {usage?.chatMessages ?? 0}
          </div>
          <div className="stat-label">
            Chat Messages
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {usage?.plan?.callsLimit ?? 100}
          </div>
          <div className="stat-label">
            Monthly Call Limit
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ textTransform: 'capitalize' }}>
            {usage?.plan?.tier ?? 'starter'}
          </div>
          <div className="stat-label">
            Current Plan
          </div>
        </div>
      </div>

      {/* Recent Calls */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Recent Calls</h2>
        </div>
        
        {error && (
          <div style={{ color: 'var(--error)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {recentCalls.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📞</div>
            <p>No calls recorded yet</p>
            <p style={{ fontSize: 14, marginTop: 8 }}>
              Calls will appear here once they are processed
            </p>
          </div>
        ) : (
          <div className="call-list">
            {recentCalls.map(({ transcript, summary }) => (
              <div
                key={transcript.transcriptId}
                className="call-item"
                onClick={() => navigate(`/contact/${transcript.contactId}`)}
              >
                <div className="call-item-header">
                  <div>
                    <span className="call-contact">
                      Contact {transcript.contactId.substring(0, 8)}
                    </span>
                    {summary && (
                      <span className={`sentiment ${getSentimentClass(summary.sentiment)}`} style={{ marginLeft: 8 }}>
                        {summary.sentiment}
                      </span>
                    )}
                  </div>
                  <span className="call-date">
                    {formatDate(transcript.callDate)}
                  </span>
                </div>
                <p className="call-summary">
                  {summary?.quickSummary || transcript.fullText.substring(0, 150) + '...'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
